/**
 * ReminderPermissionDialog — prompts user to grant notification/alarm permissions.
 *
 * Shows a Radix Dialog listing missing permissions with enable/later/opt-out actions.
 * Mounted in AppLayout — the hook controls visibility.
 */

import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog"
import { Button } from "@/app/components/ui/button"
import {
  useReminderPermissionPrompt,
  type PermissionEntry,
} from "./useReminderPermissionPrompt"

// ---------------------------------------------------------------------------
// Permission row
// ---------------------------------------------------------------------------

function PermissionRow({ entry }: { entry: PermissionEntry }) {
  const isGranted = entry.status === "granted"

  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
          isGranted
            ? "bg-green-500/15 text-green-600 dark:text-green-400"
            : "bg-muted text-muted-foreground"
        }`}
      >
        {isGranted ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="h-3 w-3"
          >
            <path
              fillRule="evenodd"
              d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <div className="h-2 w-2 rounded-full bg-current opacity-40" />
        )}
      </div>
      <span
        className={`text-sm ${isGranted ? "text-muted-foreground line-through" : ""}`}
      >
        {entry.label}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export function ReminderPermissionDialog() {
  const { t } = useTranslation()
  const {
    open,
    entries,
    missingPermissions,
    isRequesting,
    handleEnable,
    handleLater,
    handleOptOut,
  } = useReminderPermissionPrompt()

  if (!open || entries.length === 0) return null

  const allGranted = missingPermissions.length === 0

  return (
    <Dialog open={open} onOpenChange={() => handleLater()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("Reminder permissions")}</DialogTitle>
          <DialogDescription>
            {t(
              "ToToDo needs permission to deliver reminders and alarms on time. Enable the permissions below.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 py-2">
          {entries.map((entry) => (
            <PermissionRow key={entry.key} entry={entry} />
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {!allGranted && (
            <Button
              onClick={handleEnable}
              disabled={isRequesting}
              className="w-full"
            >
              {isRequesting ? t("Requesting…") : t("Enable")}
            </Button>
          )}

          <div className="flex w-full gap-2">
            <Button
              variant="stroke"
              onClick={handleLater}
              className="flex-1"
            >
              {t("Later")}
            </Button>
            <Button
              variant="ghost"
              onClick={handleOptOut}
              className="flex-1 text-muted-foreground"
            >
              {t("Don't ask again")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
